import Bugsnag from "@bugsnag/js";
import { Action, CaseReducer, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Draft } from "immer";
import _ from "lodash";
import objectHash from "object-hash";
import { DropResult } from "react-beautiful-dnd";
import { PartialDeep } from "type-fest";

import { insertFromTemplate } from "../editor/insertFromTemplate";
import { newChecklistDocumentFromTemplates } from "../generators/checklist";
import { newLibraryEntry } from "../generators/libraryEntry";
import { newId } from "../id/id";
import {
  AddonTemplate,
  AircraftAddonTemplate,
  BlankTemplate,
  SoftwareAddonTemplate,
  isAircraftAddonTemplate,
} from "../model/addonTemplate";
import {
  Checklist,
  ChecklistDocumentElement,
  ChecklistElement,
  PageBreak,
  isChecklist,
  isChecklistHeader,
  isChecklistItem,
  pageBreak,
} from "../model/checklistAppState";
import { SerializedChecklistDocument } from "../model/checklistSerialized";
import { ChecklistDocumentStyles, ChecklistElementStyles } from "../model/documentStyles";
import { ChecklistDocument } from "../model/editorState";
import { DocumentLayout } from "../model/layout";
import { DocumentLibrary } from "../model/library";

import { newChecklistDocument } from "./checklist";
import {
  deserializeChecklistElement,
  serializeChecklistElement,
  serializedChecklistDocument,
} from "./convert";
import { generateInitialLibraryState } from "./library";

const initialIncomingTemplate: AddonTemplate | null = null;
const initialAutofocusField: string | null = null;
const initialDraggingId: string | null = null;

export const incomingTemplateSlice = createSlice({
  name: "incomingTemplate",
  initialState: initialIncomingTemplate as AddonTemplate | null, // >:(
  reducers: {
    setIncomingTemplate: (state, action: PayloadAction<AddonTemplate | null>) => {
      return action.payload;
    },
  },
});

export const autofocusFieldSlice = createSlice({
  name: "autofocusField",
  initialState: initialAutofocusField as string | null, // >:(
  reducers: {
    setAutofocusField: (state, action: PayloadAction<string | null>) => {
      return action.payload;
    },
  },
});

// TODO: Consolidate dnd-related slices
export const draggingIdSlice = createSlice({
  name: "draggingId",
  initialState: initialDraggingId as string | null, // >:(
  reducers: {
    setDraggingId: (state, action: PayloadAction<string | null>) => {
      return action.payload;
    },
  },
});

export const isDragAndDropDraggingSlice = createSlice({
  name: "isDragAndDropDragging",
  initialState: false,
  reducers: {
    setDndDragging: (state, action: PayloadAction<boolean>) => {
      return action.payload;
    },
  },
});

export const lastReadUpdateSlice = createSlice({
  name: "lastReadUpdate",
  initialState: { id: "" },
  reducers: {
    setLastReadUpdateId: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },
  },
});

const editorAction = <A extends Action>(fn: CaseReducer<ChecklistDocument, A>) => {
  return (state: Draft<DocumentLibrary>, action: A) => {
    if (state.editingEntry !== null) {
      const editorEntry = state.entries[state.editingEntry];
      // TODO: find the logic I am missing here from redux-undo
      editorEntry.future = [];
      const presentCopy = _.cloneDeep(editorEntry.present);

      // TODO: potentially slow, rethink event duplication from VanillaEditableText
      // Maybe use useUncontrolled in VanillaEditableText?

      const res = fn(editorEntry.present, action);
      if (res) {
        editorEntry.present = res;
      }

      const afterMutation = editorEntry.present;
      const presentCopyHash = objectHash(serializedChecklistDocument(presentCopy));
      const afterMutationHash = objectHash(serializedChecklistDocument(afterMutation));
      if (presentCopyHash !== afterMutationHash) {
        while (editorEntry.past.length >= 50) {
          editorEntry.past.shift();
        }

        editorEntry.past.push(presentCopy);
        editorEntry.mtime = new Date().getTime();
      }
    } else {
      Bugsnag.notify(`Somehow editing without editingEntry being set ${action} ${state}`);
    }
  };
};

export const librarySlice = createSlice({
  name: "library",
  initialState: generateInitialLibraryState(),
  reducers: {
    setEditorEntry: (state, action: PayloadAction<number>) => {
      state.editingEntry = action.payload;
    },
    newDocument: (state) => {
      const newDocument = newLibraryEntry();
      state.entries.push(newDocument);
      state.editingEntry = state.entries.length - 1;
    },
    newDocumentFromTemplate: (
      state,
      action: PayloadAction<{
        baseTemplate: AircraftAddonTemplate | BlankTemplate;
        softwareAddons: SoftwareAddonTemplate[];
      }>
    ) => {
      const newDocument = newChecklistDocumentFromTemplates(
        isAircraftAddonTemplate(action.payload.baseTemplate)
          ? action.payload.baseTemplate.title
          : "",
        action.payload.baseTemplate,
        action.payload.softwareAddons
      );
      const newEntry = newLibraryEntry(newDocument);
      newEntry.present.title = isAircraftAddonTemplate(action.payload.baseTemplate)
        ? newDocument.title
        : "";
      state.entries.push(newEntry);
      state.editingEntry = state.entries.length - 1;
    },
    newDocumentFromSerialized: (state, action: PayloadAction<SerializedChecklistDocument>) => {
      // TODO: move this to helper function -- maybe newChecklistDocument should take a whole doc?
      // TODO: de-duplicate -- if incoming is already in library, select it instead
      const newDocument = newChecklistDocument(action.payload.contents);
      newDocument.layout = _.cloneDeep(action.payload.layout);
      newDocument.options = _.cloneDeep(action.payload.options);
      newDocument.styles = _.cloneDeep(action.payload.styles);
      newDocument.title = _.cloneDeep(action.payload.title);

      const newEntry = newLibraryEntry(newDocument);
      newEntry.present.title = action.payload.title;
      state.entries.push(newEntry);
      state.editingEntry = state.entries.length - 1;
    },
    deleteDocument: (
      state,
      action: PayloadAction<{ libraryIndex: number; documentId: string }>
    ) => {
      if (state.editingEntry === action.payload.libraryIndex) {
        state.editingEntry = null;
      }
      // Sanity check to prevent bugs from deleting the wrong document
      const targetId = state.entries[action.payload.libraryIndex].present.id;
      if (targetId !== action.payload.documentId) {
        Bugsnag.notify(
          `Error encountered deleting library entry ${action.payload.libraryIndex}: library ID ${targetId} does not match passed ${action.payload.documentId}. Refusing to delete.`
        );
      } else {
        state.entries.splice(action.payload.libraryIndex, 1);
      }
    },
    undo: (state) => {
      const documentIndex = state.editingEntry;

      if (
        documentIndex == null ||
        documentIndex < 0 ||
        documentIndex >= state.entries.length
      ) {
        Bugsnag.notify(`tried to reference invalid document index in undo ${documentIndex}`);
        return state;
      }

      const workingEntry = state.entries[documentIndex];
      const undoTarget = workingEntry.past.pop();
      if (!undoTarget) {
        Bugsnag.notify("tried to undo with no history");
        return state;
      }

      workingEntry.future.push(_.cloneDeep(workingEntry.present));
      workingEntry.present = _.cloneDeep(undoTarget);
    },
    redo: (state) => {
      const documentIndex = state.editingEntry;

      if (
        documentIndex == null ||
        documentIndex < 0 ||
        documentIndex >= state.entries.length
      ) {
        Bugsnag.notify(`tried to reference invalid document index in redo ${documentIndex}`);
        return state;
      }

      const workingEntry = state.entries[documentIndex];
      const redoTarget = workingEntry.future.pop();
      if (!redoTarget) {
        Bugsnag.notify("tried to redo with no history");
        return state;
      }

      workingEntry.past.push(_.cloneDeep(workingEntry.present));
      workingEntry.present = _.cloneDeep(redoTarget);
    },
    insertContentsFromTemplate: editorAction(
      (state, action: PayloadAction<SoftwareAddonTemplate>) => {
        insertFromTemplate(state, action.payload);
      }
    ),
    setDocumentName: editorAction((state, action: PayloadAction<string>) => {
      state.title = action.payload;
    }),
    setDocumentTitle: editorAction((state, action: PayloadAction<string>) => {
      state.contents.title.content = action.payload;
    }),
    appendChecklist: editorAction((state, action: PayloadAction<Checklist>) => {
      state.contents.checklists.push(action.payload);
    }),
    insertChecklistDocumentElement: editorAction(
      (state, action: PayloadAction<{ element: ChecklistDocumentElement; position: number }>) => {
        state.contents.checklists.splice(action.payload.position, 0, action.payload.element);
      }
    ),
    setChecklistTitle: editorAction(
      (state, action: PayloadAction<{ checklistId: string; newValue: string }>) => {
        const checklistIdx = _.findIndex(
          state.contents.checklists,
          (c) => c.id === action.payload.checklistId
        );
        if (checklistIdx >= 0) {
          const targetChecklist = state.contents.checklists[checklistIdx];
          if (isChecklist(targetChecklist)) {
            targetChecklist.title.content = action.payload.newValue;
          }
        }
      }
    ),
    removeChecklist: editorAction((state, action: PayloadAction<string>) => {
      _.remove(state.contents.checklists, (e) => e.id === action.payload);
    }),
    appendPageBreak: editorAction((state, _action: PayloadAction<PageBreak>) => {
      state.contents.checklists.push({ type: pageBreak, id: newId() });
    }),
    insertChecklistElement: editorAction(
      (
        state,
        action: PayloadAction<{ element: ChecklistElement; checklistId: string; position?: number }>
      ) => {
        const checklistIdx = _.findIndex(
          state.contents.checklists,
          (c) => c.id === action.payload.checklistId
        );
        if (checklistIdx >= 0) {
          const targetChecklist = state.contents.checklists[checklistIdx];
          if (isChecklist(targetChecklist)) {
            if (action.payload.position !== undefined) {
              targetChecklist.content.splice(
                action.payload.position + 1,
                0,
                action.payload.element
              );
            } else {
              targetChecklist.content.push(action.payload.element);
            }
          }
        }
      }
    ),
    duplicateChecklistElement: editorAction((state, action: PayloadAction<ChecklistElement>) => {
      state.contents.checklists.forEach((checklist) => {
        if (isChecklist(checklist)) {
          for (let i = 0; i < checklist.content.length; i++) {
            if (checklist.content[i].id === action.payload.id) {
              const duplicatedElement = deserializeChecklistElement(
                serializeChecklistElement(checklist.content[i])
              );
              checklist.content.splice(i + 1, 0, duplicatedElement);
              return;
            }
          }
        }
      });
    }),
    updateChecklistElement: editorAction(
      (
        state,
        // TODO: ................................................vvvvvvvvvvvvvvvvvvvvvvvvvvvvv unfuck this type
        action: PayloadAction<{
          elementId: string;
          elementField: "header" | "item" | "setting";
          newValue: string;
        }>
      ) => {
        state.contents.checklists.forEach((checklist) => {
          if (isChecklist(checklist)) {
            checklist.content.forEach((checklistElement) => {
              if (checklistElement.id === action.payload.elementId) {
                if (
                  isChecklistItem(checklistElement) &&
                  (action.payload.elementField === "item" ||
                    action.payload.elementField === "setting")
                ) {
                  checklistElement[action.payload.elementField].content = action.payload.newValue;
                } else {
                  if (
                    isChecklistHeader(checklistElement) &&
                    action.payload.elementField === "header"
                  ) {
                    checklistElement.text.content = action.payload.newValue;
                  }
                }
              }
            });
          }
        });
      }
    ),
    updateChecklistElementStyles: editorAction(
      (
        state,
        action: PayloadAction<{ elementId: string; styles: ChecklistElementStyles | null }>
      ) => {
        // TODO: to a helper fn
        state.contents.checklists.forEach((checklist) => {
          if (isChecklist(checklist)) {
            checklist.content.forEach((checklistElement) => {
              if (checklistElement.id === action.payload.elementId) {
                if (action.payload.styles !== null) {
                  checklistElement.styles = _.merge(checklistElement.styles, action.payload.styles);
                } else {
                  checklistElement.styles = undefined;
                }
              }
            });
          }
        });
      }
    ),
    removeChecklistElement: editorAction(
      (
        state,
        action: PayloadAction<{ checklistId: string; checklistElement: ChecklistElement }>
      ) => {
        const checklistIdx = _.findIndex(
          state.contents.checklists,
          (c) => c.id === action.payload.checklistId
        );

        if (checklistIdx >= 0) {
          const targetChecklist = state.contents.checklists[checklistIdx];
          if (isChecklist(targetChecklist)) {
            targetChecklist.content = targetChecklist.content.filter(
              (i) => i.id !== action.payload.checklistElement.id
            );
          }
        }
      }
    ),
    reorder: editorAction((state, action: PayloadAction<DropResult>) => {
      const sourceId = action.payload.source.droppableId;
      const sourceIndex = action.payload.source.index;
      const destinationId = action.payload.destination?.droppableId;
      const destinationIndex = action.payload.destination?.index;
      const reorderedElementType = action.payload.type;

      if (reorderedElementType === "checklistElement") {
        const sourceChecklist = _.find(state.contents.checklists, (c) => c.id === sourceId);
        const destinationChecklist = _.find(
          state.contents.checklists,
          (c) => c.id === destinationId
        );

        if (
          sourceChecklist &&
          destinationChecklist &&
          isChecklist(sourceChecklist) &&
          isChecklist(destinationChecklist) &&
          destinationIndex !== undefined
        ) {
          const removedArray = sourceChecklist.content.splice(sourceIndex, 1);
          const removedElement = removedArray[0];

          destinationChecklist.content.splice(destinationIndex, 0, removedElement);
        }
      }
      if (reorderedElementType === "checklist") {
        if (destinationIndex !== undefined) {
          const removedArray = state.contents.checklists.splice(sourceIndex, 1);
          const removedElement = removedArray[0];

          state.contents.checklists.splice(destinationIndex, 0, removedElement);
        }
      }
    }),
    setDocumentLayout: editorAction((state, action: PayloadAction<DocumentLayout>) => {
      state.layout = action.payload;
    }),
    setDocumentStyles: editorAction(
      (state, action: PayloadAction<PartialDeep<ChecklistDocumentStyles> | null>) => {
        if (action.payload !== null) {
          state.styles = _.merge(state.styles, action.payload);
        } else {
          state.styles = {};
        }
      }
    ),
    setDocumentFontFamily: editorAction(
      (
        state,
        action: PayloadAction<ChecklistDocumentStyles["checklistDocument"]["fontFamily"]>
      ) => {
        state.styles.checklistDocument = state.styles.checklistDocument || {};
        state.styles.checklistDocument.fontFamily = action.payload;
      }
    ),
    setDocumentTitleFontSize: editorAction(
      (
        state,
        action: PayloadAction<ChecklistDocumentStyles["checklistDocumentTitle"]["fontSize"]>
      ) => {
        state.styles.checklistDocumentTitle = state.styles.checklistDocumentTitle || {};
        state.styles.checklistDocumentTitle.fontSize = action.payload;
      }
    ),
    setDocumentTitleFontWeight: editorAction(
      (
        state,
        action: PayloadAction<ChecklistDocumentStyles["checklistDocumentTitle"]["fontWeight"]>
      ) => {
        state.styles.checklistDocumentTitle = state.styles.checklistDocumentTitle || {};
        if (action.payload !== undefined) {
          state.styles.checklistDocumentTitle.fontWeight = action.payload;
        } else {
          delete state.styles.checklistDocumentTitle.fontWeight;
        }
      }
    ),
    setDocumentPadding: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistDocument"]["padding"]>) => {
        state.styles.checklistDocument = state.styles.checklistDocument || {};
        state.styles.checklistDocument.padding = action.payload;
      }
    ),
    setChecklistHeaderTopMargin: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistHeader"]["marginTop"]>) => {
        state.styles.checklistHeader = state.styles.checklistHeader || {};
        state.styles.checklistHeader.marginTop = action.payload;
      }
    ),
    setChecklistItemVerticalMargin: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistItem"]["marginTop"]>) => {
        state.styles.checklistItem = state.styles.checklistItem || {};
        state.styles.checklistItem.marginTop = action.payload;
        state.styles.checklistItem.marginBottom = action.payload;
      }
    ),
    setChecklistTitleFontSize: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistTitle"]["fontSize"]>) => {
        state.styles.checklistTitle = state.styles.checklistTitle || {};
        state.styles.checklistTitle.fontSize = action.payload;
      }
    ),
    setChecklistTitleFontWeight: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistTitle"]["fontWeight"]>) => {
        state.styles.checklistTitle = state.styles.checklistTitle || {};
        if (action.payload !== undefined) {
          state.styles.checklistTitle.fontWeight = action.payload;
        } else {
          delete state.styles.checklistTitle.fontWeight;
        }
      }
    ),
    setChecklistHeaderFontSize: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistHeader"]["fontSize"]>) => {
        state.styles.checklistHeader = state.styles.checklistHeader || {};
        state.styles.checklistHeader.fontSize = action.payload;
      }
    ),
    setChecklistHeaderFontWeight: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistHeader"]["fontWeight"]>) => {
        state.styles.checklistHeader = state.styles.checklistHeader || {};
        if (action.payload !== undefined) {
          state.styles.checklistHeader.fontWeight = action.payload;
        } else {
          delete state.styles.checklistHeader.fontWeight;
        }
      }
    ),
    setChecklistItemFontSize: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistItem"]["fontSize"]>) => {
        state.styles.checklistItem = state.styles.checklistItem || {};
        state.styles.checklistItem.fontSize = action.payload;
      }
    ),
    setChecklistItemFontWeight: editorAction(
      (state, action: PayloadAction<ChecklistDocumentStyles["checklistItem"]["fontWeight"]>) => {
        state.styles.checklistItem = state.styles.checklistItem || {};
        if (action.payload !== undefined) {
          state.styles.checklistItem.fontWeight = action.payload;
        } else {
          delete state.styles.checklistItem.fontWeight;
        }
      }
    ),
    setDocumentOptions: editorAction(
      (state, action: PayloadAction<PartialDeep<ChecklistDocument["options"]>>) => {
        _.assign(state.options, action.payload);
      }
    ),
  },
});
